import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Card,
  Stack,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import { useDispatch } from "src/redux/store";
import useOnboardingProgress from "src/hooks/useOnboardingProgress";
import {
  fetchOnboardingProgress,
  completeOnboardingStep,
  dismissOnboardingForToday,
  logOnboardingEvent,
} from "src/redux/slices/onboarding";

import StepEmpresa from "./steps/StepEmpresa";
import StepSubscription from "./steps/StepSubscription";
import StepLocal from "./steps/StepLocal";
import StepCarta from "./steps/StepCarta";
import StepHardware from "./steps/StepHardware";
import StepCheckout from "./steps/StepCheckout";
import StepFinish from "./steps/StepFinish";

// ----------------------------------------------------------------------
//
//   Contenido del wizard de onboarding extraído a componente reutilizable
//   (Fix3 · 2026-07-22): se monta tanto en la ruta legacy `/wizard`
//   (post-registro público) como en el modal `OnboardingWizardModal`
//   invocado desde el TopBar / dashboard. Sin `<Page>` ni `<Container>`
//   para que se adapte al layout del padre.
//
// ----------------------------------------------------------------------

// Wizard reducido a 7 steps (2026-07-28 · antes eran 13). Los steps
// afines se agrupan con tabs internas para no cansar al user con un
// pasillo eterno. `required=true` significa que el step no se puede
// saltar aunque el wizard esté en modo `mandatory` (user nuevo).
//
// Fusiones aplicadas:
//   · welcome → eliminado (no aportaba, la primera acción real ya es
//     "Datos empresa").
//   · plan + addons + support → `subscription` con 3 tabs.
//   · zonas + mesas → `local` con 2 tabs.
//   · impresoras + worker → `hardware` con 2 tabs.
//   · asistencia → integrada como CTA secundario dentro de `finish`.
export const STEPS = [
  { code: "empresa", label: "Datos empresa", component: StepEmpresa, required: true },
  { code: "subscription", label: "Suscripción", component: StepSubscription, required: true },
  { code: "local", label: "Local", component: StepLocal, required: false },
  { code: "carta", label: "Carta", component: StepCarta, required: false },
  { code: "hardware", label: "Hardware", component: StepHardware, required: false },
  { code: "checkout", label: "Pago", component: StepCheckout, required: true },
  { code: "finish", label: "¡Bienvenido!", component: StepFinish, required: true },
];

// Códigos de step legacy (13-step wizard v1) que aún pueden aparecer
// en `progress.completedSteps` de users antiguos — se mantienen
// aceptados en el backend (`StepOrder`) para compatibilidad pero ya
// no se renderizan en el wizard nuevo.

OnboardingWizardContent.propTypes = {
  /** Se dispara cuando termina el wizard (done) o el user pulsa "Recordar mañana". */
  onClose: PropTypes.func,
  /** True cuando el wizard es OBLIGATORIO para el user (nuevo, sin
   * mesas). Oculta "Recordar mañana" y "Saltar" — el user debe
   * completar los pasos antes de trabajar con el sistema. */
  mandatory: PropTypes.bool,
};


export default function OnboardingWizardContent({ onClose, mandatory = false }) {
  const dispatch = useDispatch();
  const { progress, loading, hasPendingCheckout } = useOnboardingProgress();
  // Override manual del step activo cuando el user clica un chip.
  // Se resetea si `forcedIdx` cambia (para no bloquear la escalera
  // cuando el user completa un step y el sistema recalcula).
  const [manualIdx, setManualIdx] = useState(null);

  useEffect(() => {
    dispatch(fetchOnboardingProgress());
    dispatch(logOnboardingEvent("global", "entered", { dataJson: JSON.stringify({ from: "wizard-content" }) }));
  }, [dispatch]);

  // ─── Navegación del wizard ─────────────────────────────────────
  // Fuente de verdad principal: `progress.currentStep` del backend.
  // Cada "Guardar" del step actual llama a `completeOnboardingStep`,
  // que en backend incrementa `CurrentStep` → aquí renderizamos el
  // siguiente step de la secuencia natural (no el que "falta" — el
  // user quiere iterar lineal aunque los intermedios estén vacíos).
  //
  // Rescate: si `hasPendingCheckout=true` Y `currentStep` apunta más
  // allá de `checkout` (bug histórico donde `done` se marcó sin
  // pagar), forzamos volver al checkout. Este rescate SOLO pisa la
  // navegación en ese caso edge — no interfiere con el flujo normal.
  //
  // El user puede además clicar cualquier chip de un step YA
  // COMPLETADO para volver atrás. No puede saltar hacia adelante
  // (steps futuros no clicables).
  //
  // ¡OJO! Este cálculo va ANTES del early return `if (loading || !progress)`
  // porque debajo hay un `useEffect` que depende de `forcedIdx` — los
  // hooks deben ejecutarse en el mismo orden en cada render (rules-of-hooks).
  const checkoutIdxCanonical = STEPS.findIndex((s) => s.code === "checkout");
  const currentStepFromProgress = progress
    ? Math.max(
        0,
        Math.min(STEPS.length - 1, progress.currentStep < 0 ? 0 : progress.currentStep),
      )
    : 0;
  const forcedIdx =
    progress && hasPendingCheckout && currentStepFromProgress > checkoutIdxCanonical
      ? checkoutIdxCanonical
      : -1;

  // Reset del override manual cuando el sistema recalcula el forcedIdx
  // (típicamente: rescate activado o desactivado por cambio de sub).
  useEffect(() => {
    setManualIdx(null);
  }, [forcedIdx]);

  if (loading || !progress) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  // manualIdx prevalece si el user clicó un chip (navegación libre
  // hacia atrás por steps completados).
  const idx = manualIdx !== null
    ? manualIdx
    : forcedIdx >= 0
      ? forcedIdx
      : currentStepFromProgress;
  const step = STEPS[idx];
  const StepComponent = step.component;

  const handleComplete = async (extra = {}) => {
    await dispatch(completeOnboardingStep(step.code, extra));
    // Reset del override manual: el user completó el step actual,
    // el sistema debe renderizar el siguiente según `progress.currentStep`
    // (que el backend ya incrementó). Sin este reset, si el user
    // llegó al step actual clicando un chip (manualIdx != null), el
    // idx computed seguía anclado a ese chip y el botón "Ya lo tengo"
    // parecía no hacer nada aunque el backend avanzase.
    setManualIdx(null);
    if (idx === STEPS.length - 1) {
      await dispatch(completeOnboardingStep("done"));
      onClose?.();
    }
  };
  const handleSkip = async () => {
    await dispatch(logOnboardingEvent(step.code, "skipped"));
    if (idx < STEPS.length - 1) {
      await dispatch(
        completeOnboardingStep(step.code, {
          dataJson: JSON.stringify({ skipped: true }),
        }),
      );
      setManualIdx(null);
    }
  };
  const handleDismiss = async () => {
    await dispatch(dismissOnboardingForToday());
    onClose?.();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header con progreso */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Chip label="Configuración inicial" color="warning" sx={{ fontWeight: 700, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Vamos a preparar tu TPV
          </Typography>
        </Box>
        <Box sx={{ textAlign: { xs: "left", sm: "right" }, width: { xs: "100%", sm: "auto" } }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {progress.progressPercent}% completado
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress.progressPercent}
            color="success"
            sx={{ width: { xs: "100%", sm: 240 }, mt: 0.5, height: 6, borderRadius: 3 }}
          />
        </Box>
      </Stack>

      {/* Stepper de chips. Los chips de steps ya COMPLETADOS son
          clicables — el user puede volver atrás a revisar/ajustar
          lo que ya guardó. Los steps futuros NO son clicables (no
          se puede saltar hacia adelante sin haber pasado por ellos).
          El step `checkout` tiene extra guard: hasta que la sub esté
          activa, no es clicable como "completado" (evita que el user
          pinche checkout y salte a `finish` sin haber pagado). */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 3 }}>
        {STEPS.map((s, i) => {
          const done = (progress.completedSteps || []).includes(s.code);
          const isCurrent = i === idx;
          // checkout: aunque el backend lo marque completed, requiere
          // sub activa para poder saltar hacia adelante (StepFinish).
          const checkoutReady = s.code !== "checkout" || !hasPendingCheckout;
          const clickable = !isCurrent && done && checkoutReady;
          return (
            <Chip
              key={s.code}
              label={`${i + 1}. ${s.label}`}
              size="small"
              variant={isCurrent ? "filled" : "outlined"}
              color={done ? "success" : isCurrent ? "warning" : "default"}
              onClick={clickable ? () => setManualIdx(i) : undefined}
              sx={{
                fontWeight: 700,
                cursor: clickable ? "pointer" : "default",
                opacity: !isCurrent && !done ? 0.6 : 1,
              }}
            />
          );
        })}
      </Stack>

      {/* Step actual */}
      <Card sx={{ p: { xs: 2, md: 4 }, minHeight: 320 }}>
        <StepComponent
          progress={progress}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      </Card>

      {/* Nav inferior.
          · "Recordar mañana" se oculta en modo `mandatory` (user nuevo).
          · "Saltar este paso" ELIMINADO — cada step ya tiene su
            propio botón (Saltar / Completar después / Configurar
            después). Además, los steps agrupados con tabs internas
            iteran por tab con su propio Saltar; el nav global saltaba
            el step entero rompiendo la iteración. */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        {mandatory ? (
          <span />
        ) : (
          <Button onClick={handleDismiss} color="inherit">
            Recordar mañana
          </Button>
        )}
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Paso {idx + 1} de {STEPS.length}
        </Typography>
        <span />
      </Stack>
    </Box>
  );
}
